/**
 * Partner Overview Details (TMMPDM-0085): one header row and one data row.
 * Absent values are an em dash. The container scrolls sideways; the page does not.
 */
import type { ReactNode } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import type { TenantView } from '@/api/tmm/types';
import { formatInstant } from '@/format';

const ABSENT = '—';

function column(label: string) {
  return (
    <TableCell component="th" scope="col">
      {label}
    </TableCell>
  );
}

export function PartnerDetailsSection({
  tenant,
  brand,
  onEdit,
}: {
  tenant: TenantView | null;
  brand: ReactNode;
  onEdit?: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const created = tenant ? formatInstant(tenant.createdAt, i18n.language, 'date') : ABSENT;
  const ai = tenant
    ? tenant.aiEnabledDefault
      ? t('tenantDetail.overview.yes', 'Yes')
      : t('tenantDetail.overview.no', 'No')
    : ABSENT;

  return (
    <Box component="section" aria-labelledby="reseller-details">
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
      >
        <Typography id="reseller-details" variant="subtitle1" component="h2">
          {t('resellers.overview.details', 'Details')}
        </Typography>
        {onEdit ? (
          <Button size="small" onClick={onEdit}>
            {t('tenants.edit.button', 'Edit…')}
          </Button>
        ) : null}
      </Stack>
      <TableContainer
        component={Paper}
        variant="outlined"
        data-testid="partner-details-scroll"
        sx={{ overflowX: 'auto' }}
      >
        <Table
          size="small"
          aria-label={t('resellers.overview.details', 'Details')}
          sx={{ minWidth: 720 }}
        >
          <TableHead>
            <TableRow>
              {column(t('resellers.overview.brand', 'Brand'))}
              {column(t('resellers.overview.visibility', 'Visibility'))}
              {column(t('tenantDetail.invitations.col.created', 'Created'))}
              {column(t('tenantDetail.overview.currency', 'Currency'))}
              {column(t('tenantDetail.overview.aiDefault', 'AI on by default'))}
              {column(t('tenantDetail.overview.emailFromName', 'Email from name'))}
              {column(t('tenantDetail.overview.emailFromAddress', 'Email from address'))}
              {column(t('tenantDetail.overview.emailSupportEmail', 'Support email'))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{brand}</TableCell>
              <TableCell>
                {tenant ? (
                  <Chip
                    size="small"
                    label={t(`tenants.signupMode.${tenant.signupMode}`, tenant.signupMode)}
                  />
                ) : (
                  ABSENT
                )}
              </TableCell>
              <TableCell>{created}</TableCell>
              <TableCell>{tenant ? tenant.currency : ABSENT}</TableCell>
              <TableCell>{ai}</TableCell>
              <TableCell>{tenant?.emailFromName ?? ABSENT}</TableCell>
              <TableCell>{tenant?.emailFromAddress ?? ABSENT}</TableCell>
              <TableCell>{tenant?.emailSupportEmail ?? ABSENT}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('resellers.overview.brandEditedIn', 'The brand is edited in saas-admin.')}
      </Typography>
      <Typography role="note" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t(
          'tenants.deleteNote',
          'To suspend or delete a tenant, ask the platform operator (saas-admin).',
        )}
      </Typography>
    </Box>
  );
}
